import { useMemo, useState } from 'react';
import {
  ExampleCallout,
  FieldLabel,
  InfoSection,
  PanelTitle,
  ResultsEmpty,
  ToolPageHeading,
  ToolTwoColumn,
  fmtUsd,
  parseNum,
  toolInputClass,
} from './toolShared';

const DEFAULTS = {
  units: '',
  rent: '',
  other: '',
  occ: '95',
  opex: '',
  cap: '7',
};

export function ToolUnderwriting() {
  const [v, setV] = useState(DEFAULTS);

  const results = useMemo(() => {
    const units = parseNum(v.units);
    const rent = parseNum(v.rent);
    const other = parseNum(v.other);
    const occ = Math.min(100, Math.max(0, parseNum(v.occ))) / 100;
    const opex = parseNum(v.opex);
    const cap = parseNum(v.cap) / 100;
    if (units <= 0 || rent <= 0 || cap <= 0) return null;
    const egi = units * rent * 12 * occ + other;
    const noi = egi - opex;
    const offer = noi / cap;
    return { egi, noi, offer };
  }, [v]);

  return (
    <div className="space-y-8">
      <ToolPageHeading
        title="Underwriting calculator"
        subtitle="Estimate NOI from rent roll inputs, then solve for offer price at your target cap rate."
      />

      <InfoSection title="How to use this calculator">
        <p>
          Gross potential rent is adjusted by occupancy; other income is added. Operating expenses are subtracted to arrive
          at NOI. Offer price reverses the cap rate formula:{' '}
          <span className="font-mono text-[var(--home-miami-blue)]">Price ≈ NOI ÷ Target cap rate</span>.
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong className="text-[var(--home-text-on-dark)]">Average rent</strong> — Per unit per month, in-place or
            pro forma.
          </li>
          <li>
            <strong className="text-[var(--home-text-on-dark)]">Target cap rate</strong> — Your required going-in yield.
          </li>
        </ul>
        <ExampleCallout>
          This is a teaching calculator — always verify T-12, reserves, and market comps before submitting an LOI.
        </ExampleCallout>
      </InfoSection>

      <ToolTwoColumn
        onReset={() => setV(DEFAULTS)}
        left={
          <>
            <PanelTitle>Property details</PanelTitle>
            <div className="mb-6 grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <FieldLabel htmlFor="uw-u" tip="Number of units in the rent roll." required>
                  Number of units
                </FieldLabel>
                <input
                  id="uw-u"
                  type="text"
                  inputMode="decimal"
                  value={v.units}
                  onChange={(e) => setV((s) => ({ ...s, units: e.target.value }))}
                  className={toolInputClass}
                  placeholder="0"
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <FieldLabel htmlFor="uw-r" tip="Average in-place or market rent per unit per month." required>
                  Average rent per month
                </FieldLabel>
                <div className="flex rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <span className="flex items-center pl-3 font-body text-sm text-[var(--home-muted-on-dark)]">$</span>
                  <input
                    id="uw-r"
                    type="text"
                    inputMode="decimal"
                    value={v.rent}
                    onChange={(e) => setV((s) => ({ ...s, rent: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent pl-1 shadow-none ring-0 focus:ring-0`}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
            <PanelTitle>Income &amp; expenses</PanelTitle>
            <div className="mb-6 space-y-4">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="uw-o" tip="Laundry, parking, fees — annual." >
                  Annual other income
                </FieldLabel>
                <div className="flex rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <span className="flex items-center pl-3 font-body text-sm text-[var(--home-muted-on-dark)]">$</span>
                  <input
                    id="uw-o"
                    type="text"
                    inputMode="decimal"
                    value={v.other}
                    onChange={(e) => setV((s) => ({ ...s, other: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent pl-1 shadow-none ring-0 focus:ring-0`}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="uw-occ" tip="Economic occupancy (0–100%).">
                  Occupancy rate
                </FieldLabel>
                <div className="flex items-center rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <input
                    id="uw-occ"
                    type="text"
                    inputMode="decimal"
                    value={v.occ}
                    onChange={(e) => setV((s) => ({ ...s, occ: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent shadow-none ring-0 focus:ring-0`}
                    placeholder="95.00"
                  />
                  <span className="pr-3 font-body text-sm text-[var(--home-muted-on-dark)]">%</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="uw-x" tip="Annual operating expenses before debt and reserves." required>
                  Annual operating expenses
                </FieldLabel>
                <div className="flex rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <span className="flex items-center pl-3 font-body text-sm text-[var(--home-muted-on-dark)]">$</span>
                  <input
                    id="uw-x"
                    type="text"
                    inputMode="decimal"
                    value={v.opex}
                    onChange={(e) => setV((s) => ({ ...s, opex: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent pl-1 shadow-none ring-0 focus:ring-0`}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
            <PanelTitle>Target return</PanelTitle>
            <div className="space-y-1.5">
              <FieldLabel htmlFor="uw-cap" tip="Required going-in cap rate (%)." required>
                Target cap rate
              </FieldLabel>
              <div className="flex items-center rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                <input
                  id="uw-cap"
                  type="text"
                  inputMode="decimal"
                  value={v.cap}
                  onChange={(e) => setV((s) => ({ ...s, cap: e.target.value }))}
                  className={`${toolInputClass} border-0 bg-transparent shadow-none ring-0 focus:ring-0`}
                  placeholder="7.00"
                />
                <span className="pr-3 font-body text-sm text-[var(--home-muted-on-dark)]">%</span>
              </div>
            </div>
          </>
        }
        right={
          <>
            <PanelTitle>Underwriting results</PanelTitle>
            {results === null ? (
              <ResultsEmpty message="Enter property details to calculate offer price." />
            ) : (
              <div className="flex min-h-[260px] flex-col justify-center gap-5">
                <div>
                  <p className="font-body text-xs uppercase tracking-wide text-[var(--home-muted-on-dark)]">Effective gross income</p>
                  <p className="font-display text-xl font-bold text-[var(--home-text-on-dark)]">{fmtUsd(results.egi)}</p>
                </div>
                <div>
                  <p className="font-body text-xs uppercase tracking-wide text-[var(--home-muted-on-dark)]">NOI</p>
                  <p className="font-display text-xl font-bold text-[var(--home-text-on-dark)]">{fmtUsd(results.noi)}</p>
                </div>
                <div>
                  <p className="font-body text-xs uppercase tracking-wide text-[var(--home-muted-on-dark)]">
                    Indicative offer (NOI ÷ cap)
                  </p>
                  <p className="font-display text-2xl font-bold text-emerald-300">{fmtUsd(results.offer)}</p>
                </div>
              </div>
            )}
          </>
        }
      />
    </div>
  );
}
